import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import api from '@/lib/api';

/**
 * Reusable Trust Score badge. Fetches /analytics/trust-score once on mount
 * (cheap, aggregation on a single collection) and renders a coloured pill
 * with a hover tooltip breaking down the signals.
 */
export function TrustScoreBadge({ pageName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/analytics/trust-score')
      .then(res => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return null;
  if (!data || !data.has_data) return null;

  const { score, grade, total_records, signals, remediation } = data;
  const color = score >= 85 ? 'bg-emerald-500'
    : score >= 70 ? 'bg-lime-500'
    : score >= 55 ? 'bg-amber-500'
    : 'bg-rose-500';
  const Icon = score >= 70 ? ShieldCheck : score >= 55 ? Shield : ShieldAlert;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="inline-flex items-center gap-1.5 cursor-help"
            data-testid={`trust-score-badge${pageName ? `-${pageName}` : ''}`}
          >
            <Badge className={`${color} text-white hover:opacity-90 border-0 px-2.5 py-1`}>
              <Icon className="w-3.5 h-3.5 mr-1" />
              Data Trust: {score} · {grade}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm p-3 text-xs">
          <div className="font-semibold mb-1.5">
            Based on {total_records?.toLocaleString()} records
          </div>
          <div className="space-y-0.5 mb-2">
            <SignalRow label="Classification coverage" value={signals.classification_coverage} />
            <SignalRow label="Field completeness" value={signals.field_completeness} />
            <SignalRow label="AI confidence" value={signals.confidence} />
            <SignalRow label="Supplier normalization" value={signals.supplier_normalization} />
            <SignalRow label="Hierarchy consistency" value={signals.hierarchy_consistency} />
            <SignalRow label="Vocabulary stability" value={signals.vocabulary_stability} />
          </div>
          {remediation?.length > 0 && (
            <div className="border-t border-[hsl(var(--primary-foreground)/0.25)] pt-1.5 mt-1.5">
              <div className="font-semibold mb-1">To improve:</div>
              <ul className="space-y-1 text-[hsl(var(--primary-foreground)/0.85)]">
                {remediation.slice(0, 3).map((r, i) => (
                  <li key={i}>• {r.hint}</li>
                ))}
              </ul>
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SignalRow({ label, value }) {
  if (value === null || value === undefined) {
    return (
      <div className="flex justify-between">
        <span>{label}</span>
        <span className="text-[hsl(var(--primary-foreground)/0.6)]">N/A</span>
      </div>
    );
  }
  const color = value >= 85 ? 'text-emerald-300'
    : value >= 70 ? 'text-lime-300'
    : value >= 55 ? 'text-amber-300'
    : 'text-rose-300';
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={`font-mono ${color}`}>{value}%</span>
    </div>
  );
}
